import { FileUpload } from "@/components/elements/file-upload";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";
import { useNotifyFileChange } from "@/hooks/livekit/useNotifyFileChange";
import type { FileWithPreview } from "@/types/public";
import { toaster } from "@/utils/toaster";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const origin = import.meta.env.VITE_BIOARC_SERVICE || "https://bioarc.ir";
interface Props {
  refetch: () => void;
}
export default function UploadFileDialog({ refetch }: Props) {
  const [showModal, setShowModal] = useState<boolean>();
  const { t } = useTranslation();
  const { focusedhash } = useSelector(selectCurrentRoom);
  const { user } = useSelector(selectCurrentAuth);
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};

  const handleFilesChange = (files: FileWithPreview[]) => {
    console.log("Files updated:", files);
  };
  const { notifyFileChange } = useNotifyFileChange({
    user: room?.localParticipant,
    room,
  });
  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogTrigger asChild>
        <Button
          variant={"default"}
          className={"flex gap-3 items-center w-full"}
        >
          <Upload size={20} className="cursor-pointer mb-1" />
          {t("upload_files")}
        </Button>
      </DialogTrigger>

      <DialogContent className="lg:min-w-[800px] max-h-dvh ">
        <DialogHeader>
          <DialogTitle>{t("upload_files")}</DialogTitle>
        </DialogHeader>
        {focusedhash ? (
          <div className="overflow-y-auto max-h-[90vh] flex flex-col gap-1.5">
            <Card className="shadow-none border-none bg-transparent sm:bg-card">
              <CardHeader>
                <CardTitle />
              </CardHeader>
              <CardContent className="px-0 md:px-5">
                <FileUpload
                  uploadUrl={`${origin}/VisitOnline/AjaxUploadFile`}
                  extraData={{
                    hash: focusedhash || "",
                    sender_id: user?.id || "",
                  }}
                  deleteUrl={`${origin}/VisitOnline/actionAjaxRemoveFile`}
                  maxFiles={2}
                  maxSize={20} // MB
                  acceptedTypes={[
                    "image/*",
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "text/plain",
                  ]}
                  onFilesChange={handleFilesChange}
                  onUploadError={(f) => {
                    let message;
                    try {
                      if (f._xhr?.responseType === "json") {
                        const res = f._xhr.response;
                        message = res.message;
                      } else {
                        const res = JSON.parse(f._xhr?.response || "{}");
                        message = res.message;
                      }
                    } catch (err) {
                      console.error("Failed to parse JSON:", err);
                      message = null;
                    }
                    toaster("error", message);
                  }}
                  extraCallback={() => {
                    refetch();
                    notifyFileChange("file", null, "dec");
                    setShowModal(false);
                  }}
                  onUploadSuccess={() => {
                    refetch();
                    notifyFileChange("file", null, "add");
                  }}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[90vh] gap-1.5 flex justify-center py-10">
            <div className="border border-orange-500 p-1 px-2 rounded-md bg-orange-500/10 text-orange-500 text-center w-fit ">
              {t("focus_on_patients_for_file_upload")}.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
